import { getAppConfig } from '@/lib/config';
import { gzipUploadFile } from '@/lib/custom-dashboard/upload-compression';
import { ApiError } from './client';

/** タブグループ（T-0046）。一覧の `groups` は表示順に並ぶ。 */
export interface CustomDashboardTabGroup {
  groupId: string;
  name: string;
}

export interface CustomDashboardTab {
  tabId: string;
  groupId: string;
  name: string;
  activeRevision: number;
  updatedAt: string;
}

/** `GET /tabs`（表示用）。`groups` は公開中のタブがあるグループだけ、`tabs` はグループの順 → グループ内の順。 */
export interface CustomDashboardTabsConfig {
  groups: CustomDashboardTabGroup[];
  tabs: CustomDashboardTab[];
}

export interface CustomDashboardLatestRevision {
  revision: number;
  status: string;
  createdAt: string;
}

/**
 * データ更新ジョブの状態（running: 実行中 / succeeded: 成功 / failed: 失敗・キャンセル・時間切れ）。
 * `queued`（受付中）は API は返さず、開始した直後、ジョブの行（launcher Lambda が作る）が一覧に現れるまでの間だけ
 * FE が表示に使う。
 */
export type DataUpdateJobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

/** データ更新ジョブの起動元（force: タブを指定した起動（UI など）/ batch: 定期バッチからの起動。T-0059）。 */
export type DataUpdateTrigger = 'force' | 'batch';

/** 自動公開（autoActivate）の結果。`pending` は実行中の autoActivate ジョブ。 */
export type DataUpdateActivation = 'not_requested' | 'pending' | 'activated' | 'skipped_conflict';

export interface DataUpdateJobError {
  code: string;
  message: string;
}

export interface DataUpdateJob {
  jobId: string;
  accountCode: string;
  tabId: string;
  /** 更新元の revision 番号（revision が見つからなければ null） */
  baseRevision: number | null;
  status: DataUpdateJobStatus;
  trigger: DataUpdateTrigger;
  autoActivate: boolean;
  activation: DataUpdateActivation;
  resultRevision: number | null;
  error: DataUpdateJobError | null;
  usage: Record<string, unknown> | null;
  summary: string | null;
  agentNote: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

/** `GET /tabs/all` にタブごと同梱される直近ジョブの要約。 */
export interface LatestDataUpdate {
  jobId: string;
  status: DataUpdateJobStatus;
  trigger: DataUpdateTrigger;
  createdAt: string;
  finishedAt: string | null;
  resultRevision: number | null;
  error: DataUpdateJobError | null;
}

/** タブの定期更新の設定（T-0059）。曜日は最終データ連携日の曜日で、ISO 8601 の番号（1 = 月 〜 7 = 日）。 */
export interface TabUpdateSettings {
  /** 更新する曜日（1〜7。昇順）。 */
  weekdays: number[];
  /** 定期バッチから起動したジョブの結果を、成功時に自動で公開する。 */
  autoActivate: boolean;
}

export interface ManagedCustomDashboardTab {
  tabId: string;
  /** 属するグループ。グループ未指定のタブ（閲覧画面に出ない）は null。 */
  groupId: string | null;
  name: string;
  activeRevision: number | null;
  /** active revision が ready かつ details.txt を持つか（データ更新ボタンの有効条件）。 */
  activeRevisionHasDetails: boolean;
  updatedAt: string;
  latestRevision: CustomDashboardLatestRevision | null;
  latestDataUpdate: LatestDataUpdate | null;
  /** 定期更新の設定。設定の無いタブ（定期更新しない）は null。 */
  updateSettings: TabUpdateSettings | null;
}

/**
 * `GET /tabs/all`（設定用）。`groups` は空のグループも含めて表示順に、`tabs` はグループの順 → グループ内の順に並び、
 * グループ未指定のタブは作成順に末尾に来る。
 */
export interface ManagedCustomDashboardTabsConfig {
  groups: CustomDashboardTabGroup[];
  tabs: ManagedCustomDashboardTab[];
}

/**
 * `PUT /tab-layout` の body。残すグループ（表示順）と各グループのタブ（表示順）、グループ未指定のタブ、
 * 削除するグループとタブをまとめて送る。グループ・タブそれぞれの和集合が保存時点の全体と一致しなければ 409。
 */
export interface CustomDashboardTabLayout {
  groups: { groupId: string; tabIds: string[] }[];
  ungroupedTabIds: string[];
  deleteGroupIds: string[];
  deleteTabIds: string[];
}

export interface CustomDashboardRevision {
  revision: number;
  status: string;
  createdAt: string;
  readyAt: string | null;
  htmlBytes: number;
  dataBytes: number;
  source: 'upload' | 'agent';
}

export interface UploadCustomDashboardResponse {
  tabId: string;
  revision: number;
  status: string;
  name: string;
  htmlBytes: number;
  dataBytes: number;
  detailsBytes: number | null;
}

export interface ActivateCustomDashboardRevisionResponse {
  tabId: string;
  activeRevision: number;
}

interface ErrorEnvelope {
  error: { code: string; message: string; currentRevision?: number };
}

/** 公開 revision（`expectedActiveRevision`）の競合かどうか（公開切替・データ更新の開始）。 */
export function isRevisionConflict(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return (err as ApiError & { data?: ErrorEnvelope }).data?.error?.code === 'revision_conflict';
}

/** 409（配置の保存で、ほかのユーザーがタブ・グループを追加・削除した・データ更新中のタブを削除しようとした）かどうか。 */
export function isConflict(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409;
}

/** 同一タブに実行中のデータ更新ジョブが既にあるための409かどうか。 */
export function isDataUpdateConflict(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return (err as ApiError & { data?: ErrorEnvelope }).data?.error?.code === 'CONFLICT';
}

/**
 * 応答から結果が分からない失敗かどうか。BE のエラー本文（envelope）が無い 5xx（API Gateway の統合タイムアウトの
 * 503 / 504 など）と、応答を受け取れなかった通信エラーが該当する。BE が処理を終えている可能性があるため、
 * 呼び出し元は状態を読み直して確かめる。
 */
export function isUnknownOutcome(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.status >= 500 && !(err as ApiError & { data?: ErrorEnvelope }).data;
  }
  return err instanceof TypeError;
}

export const getCustomDashboardTabs = (
  accountId: string,
  authHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<CustomDashboardTabsConfig> =>
  requestJson(
    `/${encodeURIComponent(accountId)}/custom-dashboard/tabs`,
    { method: 'GET', signal },
    authHeaders,
  );

export const getAllCustomDashboardTabs = (
  accountId: string,
  authHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<ManagedCustomDashboardTabsConfig> =>
  requestJson(
    `/${encodeURIComponent(accountId)}/custom-dashboard/tabs/all`,
    { method: 'GET', signal },
    authHeaders,
  );

/**
 * 新規タブを作成する。tabId は BE が採番するため、呼び出し側はタブ名を渡す。`groupId` を渡せばそのグループの末尾に
 * 入り（グループが無ければ 404）、省略すればグループ未指定になる。各ファイルは gzip 圧縮して送る（BE が展開して検証する）。
 */
export const createCustomDashboardTab = async (
  accountId: string,
  target: { name: string; groupId?: string },
  files: { dashboard: File; data: File; details?: File },
  authHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<UploadCustomDashboardResponse> => {
  const body = await uploadFormData(files);
  body.append('name', target.name);
  if (target.groupId !== undefined) body.append('groupId', target.groupId);
  return requestJson(
    `/${encodeURIComponent(accountId)}/custom-dashboard/tabs`,
    { method: 'POST', body, signal },
    authHeaders,
    false,
  );
};

/** 既存タブに revision を追加する（削除したタブは 404）。各ファイルは gzip 圧縮して送る。 */
export const uploadCustomDashboard = async (
  accountId: string,
  tabId: string,
  files: { dashboard: File; data: File; details?: File },
  authHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<UploadCustomDashboardResponse> => {
  const body = await uploadFormData(files);
  return requestJson(
    `/${encodeURIComponent(accountId)}/custom-dashboard/tabs/${encodeURIComponent(tabId)}/upload`,
    { method: 'POST', body, signal },
    authHeaders,
    false,
  );
};

async function uploadFormData(files: {
  dashboard: File;
  data: File;
  details?: File;
}): Promise<FormData> {
  const [dashboard, data, details] = await Promise.all([
    gzipUploadFile(files.dashboard, 'dashboard'),
    gzipUploadFile(files.data, 'data'),
    files.details ? gzipUploadFile(files.details, 'details') : undefined,
  ]);
  const body = new FormData();
  body.append('dashboard', dashboard);
  body.append('data', data);
  if (details) body.append('details', details);
  return body;
}

export const activateCustomDashboardRevision = (
  accountId: string,
  tabId: string,
  body: { expectedActiveRevision: number | null; revision: number },
  authHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<ActivateCustomDashboardRevisionResponse> =>
  requestJson(
    tabPath(accountId, tabId, '/activate'),
    { method: 'POST', body: JSON.stringify(body), signal },
    authHeaders,
  );

/**
 * グループの順・タブの所属と順・グループ未指定のタブ・グループとタブの削除をまとめて保存する（`deleteTabIds` のタブは
 * revision と成果物も削除され、削除したグループの中のタブは `ungroupedTabIds` に入れる）。ほかのユーザーが追加・削除していた場合やデータ更新中のタブを削除しようとした場合は 409。成功は 204。
 */
export const saveCustomDashboardTabLayout = async (
  accountId: string,
  body: CustomDashboardTabLayout,
  authHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<void> => {
  await request(
    `/${encodeURIComponent(accountId)}/custom-dashboard/tab-layout`,
    { method: 'PUT', body: JSON.stringify(body), signal },
    authHeaders,
  );
};

/** 空のグループを表示末尾に作る（groupId は BE が採番する）。成功は 201。 */
export const createCustomDashboardTabGroup = (
  accountId: string,
  body: { name: string },
  authHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<CustomDashboardTabGroup> =>
  requestJson(
    `/${encodeURIComponent(accountId)}/custom-dashboard/tab-groups`,
    { method: 'POST', body: JSON.stringify(body), signal },
    authHeaders,
  );

/** グループ名を変更する（同時の変更は後勝ち。削除済みのグループは 404）。成功は 204。 */
export const renameCustomDashboardTabGroup = async (
  accountId: string,
  groupId: string,
  body: { name: string },
  authHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<void> => {
  await request(
    `/${encodeURIComponent(accountId)}/custom-dashboard/tab-groups/${encodeURIComponent(groupId)}`,
    { method: 'PATCH', body: JSON.stringify(body), signal },
    authHeaders,
  );
};

/** タブ名を変更する（同時の変更は後勝ち）。成功は 204。 */
export const renameCustomDashboardTab = async (
  accountId: string,
  tabId: string,
  body: { name: string },
  authHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<void> => {
  await request(
    tabPath(accountId, tabId),
    { method: 'PATCH', body: JSON.stringify(body), signal },
    authHeaders,
  );
};

export const listCustomDashboardRevisions = (
  accountId: string,
  tabId: string,
  authHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<{ revisions: CustomDashboardRevision[] }> =>
  requestJson(tabPath(accountId, tabId, '/revisions'), { method: 'GET', signal }, authHeaders);

/**
 * データ更新の開始応答。BE は起動依頼を SQS に送っただけで、ジョブの行は launcher Lambda が作る
 * （一覧にこの jobId の行が現れるまで「受付中」と表示する）。
 */
export interface QueuedDataUpdate {
  jobId: string;
  /** BE が事前チェックで確かめた、更新元にする公開 revision の番号。 */
  baseRevision: number;
  status: 'queued';
}

export const startCustomDashboardDataUpdate = (
  accountId: string,
  tabId: string,
  body: { expectedActiveRevision: number; autoActivate: boolean },
  authHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<QueuedDataUpdate> =>
  requestJson(
    tabPath(accountId, tabId, '/data-updates'),
    { method: 'POST', body: JSON.stringify(body), signal },
    authHeaders,
  );

export const cancelCustomDashboardDataUpdate = (
  accountId: string,
  tabId: string,
  jobId: string,
  authHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<{ job: DataUpdateJob }> =>
  requestJson(
    tabPath(accountId, tabId, `/data-updates/${encodeURIComponent(jobId)}`),
    { method: 'DELETE', signal },
    authHeaders,
  );

export const getCustomDashboardRevisionHtml = (
  accountId: string,
  tabId: string,
  revision: number,
  authHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<string> =>
  requestText(
    tabPath(accountId, tabId, `/revisions/${revision}/html`),
    { method: 'GET', signal },
    authHeaders,
  );

export const getCustomDashboardRevisionData = (
  accountId: string,
  tabId: string,
  revision: number,
  authHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<ArrayBuffer> =>
  requestBuffer(
    tabPath(accountId, tabId, `/revisions/${revision}/data`),
    { method: 'GET', signal },
    authHeaders,
  );

/** custom-dashboard の API モジュールで共有する（`custom-dashboard-update-settings.ts` など）。 */
export function tabPath(accountId: string, tabId: string, suffix = ''): string {
  return `/${encodeURIComponent(accountId)}/custom-dashboard/tabs/${encodeURIComponent(tabId)}${suffix}`;
}

export async function requestJson<T>(
  path: string,
  options: RequestInit,
  authHeaders: Record<string, string>,
  json = true,
): Promise<T> {
  const response = await request(path, options, authHeaders, json);
  return response.json() as Promise<T>;
}

async function requestBuffer(
  path: string,
  options: RequestInit,
  authHeaders: Record<string, string>,
): Promise<ArrayBuffer> {
  const response = await request(path, options, authHeaders, false);
  return response.arrayBuffer();
}

async function requestText(
  path: string,
  options: RequestInit,
  authHeaders: Record<string, string>,
): Promise<string> {
  const response = await request(path, options, authHeaders, false);
  return response.text();
}

async function request(
  path: string,
  options: RequestInit,
  authHeaders: Record<string, string>,
  json = true,
): Promise<Response> {
  const headers = new Headers(authHeaders);
  if (json) headers.set('Content-Type', 'application/json');
  const response = await fetch(`${getAppConfig().apiBaseUrl}${path}`, {
    mode: 'cors',
    credentials: 'include',
    ...options,
    headers,
  });
  if (!response.ok) {
    const envelope = await readErrorEnvelope(response);
    const error = new ApiError(
      envelope?.error.message ?? `API request failed: ${response.status}`,
      response.status,
      response,
    );
    if (envelope) (error as ApiError & { data?: ErrorEnvelope }).data = envelope;
    throw error;
  }
  return response;
}

async function readErrorEnvelope(response: Response): Promise<ErrorEnvelope | undefined> {
  try {
    const value: unknown = await response.json();
    if (typeof value !== 'object' || value === null || !('error' in value)) return undefined;
    if (
      typeof value.error === 'object' &&
      value.error !== null &&
      'code' in value.error &&
      typeof value.error.code === 'string' &&
      'message' in value.error &&
      typeof value.error.message === 'string'
    ) {
      return value as ErrorEnvelope;
    }
    // BE の ApiError は RevisionConflict 以外、{"error": "<msg>", "code": "..."} の
    // フラット形式を返す（backend/core/src/utils/error.rs）。ネスト形式へ正規化する。
    if (typeof value.error === 'string') {
      return {
        error: {
          code: 'code' in value && typeof value.code === 'string' ? value.code : 'unknown_error',
          message: value.error,
        },
      };
    }
  } catch {
    // JSONでないエラー本文でもHTTP statusによる分類を維持する。
  }
  return undefined;
}
